import React, { useState, useEffect, useMemo } from "react";
import { Modal, Form, Row, Col, Badge, Spinner } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-hot-toast";
import { ButtonComponent } from "../../components/elements";
import { useNavigate } from "react-router-dom";

const SELECT_CUSTOM_STYLES = {
    container: (provided) => ({ ...provided, width: "100%" }),
    control: (provided, state) => ({
        ...provided,
        height: "38px",
        borderRadius: "0.375rem",
        borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
        boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
        "&:hover": { borderColor: "#b8b9ba" }
    }),
    placeholder: (provided) => ({ ...provided, color: "#6c757d", fontSize: "0.9rem" }),
    singleValue: (provided) => ({ ...provided, fontSize: "0.9rem" })
};

const INITIAL_FORM_STATE = {
    pet_id: "",
    doctor_id: "",
    appointment_date: new Date().toISOString().split("T")[0],
    appointment_time_slot: "",
    procedure_id: "",
    notes: ""
};

const QuickAppointmentModal = ({
    show,
    onHide,
    reminder,
    AuthToken,
    onSuccess
}) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [procedures, setProcedures] = useState([]);
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [doctors, setDoctors] = useState([]);
    const [pets, setPets] = useState([]);
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [errors, setErrors] = useState({});

    const handleClose = () => {
        setFormData(INITIAL_FORM_STATE);
        setSelectedDoctor(null);
        setAvailableTimeSlots([]);
        setErrors({});
        onHide();
    };

    useEffect(() => {
        if (show) {
            setFormData(prev => ({
                ...prev,
                pet_id: reminder?.pet_id || "",
                doctor_id: reminder?.doctor_id || "",
                appointment_date: new Date().toISOString().split("T")[0]
            }));
            fetchInitialData();
        }
    }, [show, reminder]);

    useEffect(() => {
        if (selectedDoctor?.value && formData.appointment_date) {
            fetchTimeSlots();
        } else {
            setAvailableTimeSlots([]);
        }
    }, [selectedDoctor, formData.appointment_date]);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${AuthToken}` } };
            const API_BASE = process.env.REACT_APP_API_URL;

            const [proceduresRes, doctorsRes, petsRes] = await Promise.all([
                axios.get(`${API_BASE}/procedures`, config),
                axios.get(`${API_BASE}/doctors`, config),
                axios.get(`${API_BASE}/pets`, config)
            ]);

            const extractArray = (res, nestedKey) => {
                if (Array.isArray(res?.data)) return res.data;
                if (res?.data?.[nestedKey]?.data && Array.isArray(res.data[nestedKey].data)) return res.data[nestedKey].data;
                if (res?.data?.data && Array.isArray(res.data.data)) return res.data.data;
                return [];
            };

            const proceduresData = extractArray(proceduresRes, "procedures");
            const doctorsData = extractArray(doctorsRes, "doctors");
            const petsData = extractArray(petsRes, "pets");

            setProcedures(proceduresData);
            setDoctors(doctorsData);
            setPets(petsData);

            if (reminder?.doctor_id && doctorsData.length > 0) {
                const doctor = doctorsData.find(d => d?.id === reminder.doctor_id);
                if (doctor) {
                    setSelectedDoctor({ value: doctor.id, label: doctor.name || "Doctor" });
                    setFormData(prev => ({ ...prev, doctor_id: doctor.id }));
                }
            }

            if (reminder && proceduresData.length > 0) {
                autoSelectProcedure(reminder, proceduresData);
            }
        } catch (error) {
            console.error("Error fetching initial data:", error);
            toast.error("Failed to load initial data.");
        } finally {
            setLoading(false);
        }
    };

    const autoSelectProcedure = (reminderData, proceduresList) => {
        let procedureId = null;
        const reminderType = reminderData?.type?.toLowerCase() || "";
        const serviceName = reminderData?.service_name?.toLowerCase() || "";

        if (serviceName) {
            const matched = proceduresList.find(proc =>
                proc?.name?.toLowerCase().includes(serviceName) || serviceName.includes(proc?.name?.toLowerCase())
            );
            if (matched) procedureId = matched.id;
        }

        if (!procedureId && reminderType) {
            const typeMapping = {
                vaccine: ["vaccine", "vaccination"],
                deworming: ["deworming", "worm"],
                grooming: ["grooming"],
                checkup: ["checkup", "check-up", "examination"],
                followup: ["followup", "follow-up", "recheck"]
            };
            const keywords = typeMapping[reminderType] || [reminderType];

            for (const keyword of keywords) {
                const matched = proceduresList.find(proc => proc?.name?.toLowerCase().includes(keyword));
                if (matched) {
                    procedureId = matched.id;
                    break;
                }
            }
        }

        if (!procedureId) {
            const checkUp = proceduresList.find(proc =>
                proc?.name?.toLowerCase().includes("check") || proc?.name?.toLowerCase().includes("consultation")
            );
            procedureId = checkUp ? checkUp.id : (proceduresList[0]?.id || null);
        }

        if (procedureId) {
            setFormData(prev => ({ ...prev, procedure_id: procedureId }));
        }
    };

    const fetchTimeSlots = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/doctors/${selectedDoctor.value}/availability`,
                {
                    params: { date: formData.appointment_date },
                    headers: { Authorization: `Bearer ${AuthToken}` }
                }
            );

            let slots = [];
            if (response.data) {
                if (Array.isArray(response.data)) slots = response.data;
                else if (Array.isArray(response.data.slots)) slots = response.data.slots;
                else if (Array.isArray(response.data.data)) slots = response.data.data;
            }

            setAvailableTimeSlots(slots.map(slot => ({ value: slot, label: slot })));
        } catch (error) {
            console.error("Error pulling available slots:", error);
            setAvailableTimeSlots([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleSelectChange = (field, selected) => {
        setFormData(prev => ({ ...prev, [field]: selected ? selected.value : "" }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: "" }));

        if (field === "doctor_id") {
            setSelectedDoctor(selected);
            setFormData(prev => ({ ...prev, appointment_time_slot: "" }));
        }
    };

    const petOptions = useMemo(() =>
        pets.filter(p => p?.id).map(p => ({
            value: p.id,
            label: `${p.name || "Unknown"} - ${p.customer?.name || "No Client"}`
        })), [pets]);

    const doctorOptions = useMemo(() =>
        doctors.filter(d => d?.id).map(d => ({ value: d.id, label: d.name || "Unknown Doctor" })), [doctors]);

    const procedureOptions = useMemo(() =>
        procedures.filter(p => p?.id).map(p => ({ value: p.id, label: p.name || "Procedure" })), [procedures]);

    const activePet = petOptions.find(o => o.value === formData.pet_id) || null;
    const activeDoctor = doctorOptions.find(o => o.value === formData.doctor_id) || null;
    const activeProcedure = procedureOptions.find(o => o.value === formData.procedure_id) || null;
    const activeSlot = availableTimeSlots.find(o => o.value === formData.appointment_time_slot) || null;

    const handleSubmit = async () => {
        const validationFields = ["pet_id", "doctor_id", "appointment_date", "appointment_time_slot", "procedure_id"];
        for (const field of validationFields) {
            if (!formData[field]) {
                toast.error(`Please fill in all required fields.`);
                return;
            }
        }

        setSubmitting(true);
        try {
            const submitPayload = {
                pet_id: parseInt(formData.pet_id),
                doctor_id: parseInt(formData.doctor_id),
                appointment_date: formData.appointment_date,
                appointment_time_slot: formData.appointment_time_slot,
                procedure_ids: [{ procedure_id: parseInt(formData.procedure_id), price: 0 }],
                notes: formData.notes ? [formData.notes] : [],
                payments: []
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/appointments`,
                submitPayload,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (response.data?.success) {
                toast.success("Appointment created successfully.");
                handleClose();
                if (onSuccess) onSuccess();

                const appointmentId = response.data.data?.id;
                const procName = activeProcedure?.label || "Procedure";

                setTimeout(() => {
                    navigate(`/appointments/${appointmentId}`, { state: { procedureName: procName } });
                }, 800);
            } else {
                toast.error(response.data?.message || "Failed to create appointment.");
            }
        } catch (error) {
            console.error("Submission error:", error);
            if (error.response?.status === 422) {
                setErrors(error.response.data?.errors || {});
                toast.error(error.response.data?.message || "Validation error.");
            } else {
                toast.error(error.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            centered
            onHide={handleClose}
            size="lg"
            backdrop="static"
            className="border-0 shadow-lg"
        >
            <Modal.Header closeButton className="bg-light px-4 py-3 border-bottom">
                <Modal.Title className="fs-5 fw-bold text-dark d-flex align-items-center gap-2">
                    <i className="material-icons text-primary fs-4">event_note</i>
                    <span>Quick Appointment</span>
                    {reminder && (
                        <Badge bg="primary-subtle" className="text-primary fw-medium px-2 py-1 fs-7 rounded-pill border border-primary-subtle ms-2">
                            From: {reminder.type_label || "Reminder"}
                        </Badge>
                    )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-4">
                {loading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-2">
                        <Spinner animation="border" variant="primary" role="status" strokeWidth={3} />
                        <span className="text-muted small fw-medium">Loading details...</span>
                    </div>
                ) : (
                    <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                        <Row className="g-3">
                            {/* Pet Selection */}
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="text-secondary small fw-bold text-uppercase tracking-wider mb-2">
                                        Pet <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Select
                                        options={petOptions}
                                        value={activePet}
                                        onChange={(selected) => handleSelectChange("pet_id", selected)}
                                        placeholder="Select or Search Pet"
                                        isSearchable={false}
                                        isDisabled={true}
                                        isClearable={false}
                                        styles={SELECT_CUSTOM_STYLES}
                                    />
                                    {errors.pet_id && (
                                        <div className="text-danger mt-1 small">{errors.pet_id}</div>
                                    )}
                                </Form.Group>
                            </Col>

                            {/* Doctor Selection */}
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="text-secondary small fw-bold text-uppercase tracking-wider mb-2">
                                        Doctor <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Select
                                        options={doctorOptions}
                                        value={activeDoctor}
                                        onChange={(selected) => handleSelectChange("doctor_id", selected)}
                                        placeholder="Select or Search Doctor"
                                        isSearchable={true}
                                        isClearable
                                        styles={SELECT_CUSTOM_STYLES}
                                    />
                                    {errors.doctor_id && (
                                        <div className="text-danger mt-1 small">{errors.doctor_id}</div>
                                    )}
                                </Form.Group>
                            </Col>

                            {/* Appointment Date */}
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="text-secondary small fw-bold text-uppercase tracking-wider mb-2">
                                        Date <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="appointment_date"
                                        style={{ height: "38px" }}
                                        value={formData.appointment_date}
                                        onChange={handleChange}
                                        isInvalid={!!errors.appointment_date}
                                        min={new Date().toISOString().split("T")[0]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.appointment_date}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            {/* Time Slots */}
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="text-secondary small fw-bold text-uppercase tracking-wider mb-2">
                                        Time Slot <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Select
                                        options={availableTimeSlots}
                                        value={activeSlot}
                                        onChange={(selected) => handleSelectChange("appointment_time_slot", selected)}
                                        placeholder={formData.doctor_id ? "Select or Search Time Slot" : "Select doctor first"}
                                        isSearchable={true}
                                        isDisabled={!formData.doctor_id || availableTimeSlots.length === 0}
                                        styles={SELECT_CUSTOM_STYLES}
                                    />
                                    {errors.appointment_time_slot && (
                                        <div className="text-danger mt-1 small">{errors.appointment_time_slot}</div>
                                    )}
                                    {formData.doctor_id && availableTimeSlots.length === 0 && (
                                        <div className="text-warning mt-1 small d-flex align-items-center gap-1">
                                            <i className="material-icons fs-6">warning</i> No slots available for this date.
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>

                            {/* Procedure */}
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="text-secondary small fw-bold text-uppercase tracking-wider mb-2 d-flex align-items-center justify-content-between">
                                        <span>Procedure <span className="text-danger">*</span></span>
                                    </Form.Label>
                                    <Select
                                        options={procedureOptions}
                                        value={activeProcedure}
                                        onChange={(selected) => handleSelectChange("procedure_id", selected)}
                                        placeholder="Select or Search Procedure"
                                        isSearchable={true}
                                        isClearable
                                        isDisabled={!!activeProcedure}
                                        styles={SELECT_CUSTOM_STYLES}
                                    />
                                    {errors.procedure_id && (
                                        <div className="text-danger mt-1 small">{errors.procedure_id}</div>
                                    )}
                                </Form.Group>
                            </Col>

                            {/* Notes */}
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="text-secondary small fw-bold text-uppercase tracking-wider mb-2">Notes</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="notes"
                                        className="rounded-3 shadow-none"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        placeholder="Add any additional details here..."
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Modal.Body>

            <Modal.Footer className="bg-light px-4 py-3 border-top gap-2">
                <ButtonComponent
                    className="btn btn-secondary px-4 text-uppercase tracking-wider small fw-bold"
                    onClick={handleClose}
                    text="Cancel"
                    disabled={submitting}
                />
                <ButtonComponent
                    className="btn btn-primary px-4 text-uppercase tracking-wider small fw-bold shadow-sm"
                    onClick={handleSubmit}
                    disabled={submitting || loading}
                    text={submitting ? "Creating..." : "Create Appointment"}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default QuickAppointmentModal;